import React from 'react'
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom'
import { DcButton, PixelFactoryContext } from 'pixel-base'
import styled from 'styled-components'
import { useFinanceDemoMode } from '../hooks/useFinanceDemoMode'
import FinanceActionBar from './foundation/FinanceActionBar'
import FinancePageBanner from './foundation/FinancePageBanner'

const Meta = styled.div`
  color: #a16207;
  font-size: 12px;
`

const Dismiss = styled.button`
  border: 0;
  background: transparent;
  color: #92400e;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`

const getErrorMessage = (error: any) =>
  error?.response?.error || error?.error || error?.message || 'Action failed'

const FinanceDemoBanner: React.FC = () => {
  const navigationContext = React.useContext(NavigationContext)
  const navigate = navigationContext?.navigator
    ? (path: string) => navigationContext.navigator.push(path)
    : null
  const pixelFactory = React.useContext(PixelFactoryContext) as any
  const {
    demoMode,
    bannerDismissed,
    dismissBanner,
    resetDemoData,
    disableDemoMode,
  } = useFinanceDemoMode()

  if (!demoMode?.is_demo_mode || bannerDismissed) {
    return null
  }

  const openSettings = () => navigate?.('/finance/settings')
  const openDemoData = () => navigate?.('/finance/demo-data')

  const reset = async () => {
    if (
      !window.confirm(
        'Reset demo data now? This preserves finance setup but clears demo-owned records.',
      )
    ) {
      return
    }

    try {
      await resetDemoData({ confirmed: true, force: false })
      pixelFactory?.showSuccess?.(
        'Demo data cleared. Review your setup, then disable demo mode to start live usage.',
      )
    } catch (error) {
      pixelFactory?.showFailure?.(getErrorMessage(error))
    }
  }

  const disable = async () => {
    if (!window.confirm('Disable demo mode now?')) {
      return
    }

    try {
      await disableDemoMode({ confirmed: true })
      pixelFactory?.showSuccess?.(
        'Demo mode disabled. Finance is now ready for live usage.',
      )
    } catch (error) {
      pixelFactory?.showFailure?.(getErrorMessage(error))
    }
  }

  const showProfileSelection = demoMode.demo_data_status === 'not_installed'
  const showReset =
    demoMode.demo_data_status === 'installed' ||
    demoMode.cleanup_required_before_live_usage
  const showDisable =
    demoMode.demo_data_status === 'reset' &&
    !demoMode.cleanup_required_before_live_usage

  return (
    <FinancePageBanner
      tone="warning"
      title="Demo mode is enabled"
      message={
        <>
          Reset demo data before actual usage to avoid mixing demo balances with
          live accounting.
          {demoMode.demo_data_status === 'installed' &&
            demoMode.last_installed_batch && (
              <Meta>
                Demo batch: {demoMode.last_installed_batch.profile}
                {demoMode.last_installed_batch.tag
                  ? ` / ${demoMode.last_installed_batch.tag}`
                  : ''}
              </Meta>
            )}
          {demoMode.demo_data_status === 'reset' && (
            <Meta>
              Demo data has been cleared. Disable demo mode to begin live usage.
            </Meta>
          )}
        </>
      }
      dismiss={
        <Dismiss type="button" onClick={dismissBanner}>
          Dismiss for this session
        </Dismiss>
      }
      actions={
        <FinanceActionBar>
          <DcButton
            size="sm"
            variant="outline-secondary"
            onClick={openSettings}
          >
            Open settings
          </DcButton>
          {showProfileSelection && (
            <DcButton
              size="sm"
              variant="outline-success"
              onClick={openDemoData}
            >
              Choose demo profile
            </DcButton>
          )}
          {showReset && (
            <DcButton size="sm" variant="outline-warning" onClick={reset}>
              Reset demo data
            </DcButton>
          )}
          {showDisable && (
            <DcButton size="sm" variant="outline-danger" onClick={disable}>
              Disable demo mode
            </DcButton>
          )}
        </FinanceActionBar>
      }
    />
  )
}

export default FinanceDemoBanner
